// "Turn on desktop notifications" banner — mounted once in AuthedLayout,
// between the TopNav and the page.
//
// Browsers only grant notification permission from a user gesture, so the app
// can't enable this by default. Instead this nudges each person once: a slim
// strip under the nav with a single "Enable" button. It shows while browser
// permission hasn't been asked yet, or when permission is granted but the
// in-app switch (Settings → Desktop notifications) is still off. "Not now"
// hides it for a week. It stays hidden when the browser doesn't support
// notifications or the site is blocked (nothing we can do from code — the
// Settings card explains how to unblock) and on phones/tablets-in-portrait
// (< md), where the page-level Notification API isn't reliable.
//
// The decision logic lives in lib/desktop-notification-banner.ts (unit-tested);
// the preference store is lib/notification-prefs.ts, shared with the Settings
// card and the useDesktopNotifications hook that actually spawns the banners.

import { useState } from 'react';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useNotificationPrefs } from '@/lib/notification-prefs';
import { useBrowserNotificationPermission } from '@/lib/use-browser-notification-permission';
import {
  BANNER_DISMISS_KEY,
  BANNER_SNOOZE_MS,
  bannerMode,
  parseDismissedUntil,
} from '@/lib/desktop-notification-banner';

function readDismissedUntil(): number {
  try {
    return parseDismissedUntil(localStorage.getItem(BANNER_DISMISS_KEY));
  } catch {
    return 0;
  }
}

export function DesktopNotificationsBanner() {
  const { prefs, setPrefs } = useNotificationPrefs();
  const [permission, setPermission] = useBrowserNotificationPermission();
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => readDismissedUntil());
  const [busy, setBusy] = useState(false);

  const mode = bannerMode({
    permission,
    enabled: prefs.enabled,
    dismissedUntil,
    now: Date.now(),
  });
  if (!mode) return null;

  function dismiss() {
    const until = Date.now() + BANNER_SNOOZE_MS;
    setDismissedUntil(until);
    try {
      localStorage.setItem(BANNER_DISMISS_KEY, String(until));
    } catch {
      // Private mode / quota — the banner just returns on the next visit.
    }
  }

  async function enable() {
    if (busy) return;
    // Permission already granted — only the in-app switch was off.
    if (mode === 'turn-on') {
      setPrefs({ enabled: true });
      toast.success('Desktop notifications are on');
      return;
    }
    if (!('Notification' in window)) return;
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        setPrefs({ enabled: true });
        toast.success('Desktop notifications are on');
        // A real banner right away confirms it works at the OS level too
        // (Do-Not-Disturb / Focus would otherwise hide a problem until the
        // first real event).
        try {
          const hello = new Notification('Desktop notifications are on', {
            body: "You'll get a pop-up here when a client replies, an SLA is breached, or a ticket is assigned to you.",
            tag: 'pa-notif-enabled',
          });
          hello.onclick = () => hello.close();
        } catch {
          // Some browsers throw outside a normal page context — the toast above still confirms.
        }
      } else if (result === 'denied') {
        toast.message('Notifications are blocked', {
          description: 'To allow them later, click the lock icon in the address bar → Notifications → Allow.',
        });
      }
      // 'default' = the prompt was dismissed without choosing; leave the banner up.
    } catch {
      // Older Safari throws instead of returning — nothing to do.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="region"
      aria-label="Desktop notifications"
      className="hidden border-b border-tint-info-ring bg-tint-info text-tint-info-fg md:block"
    >
      <div className="mx-auto flex w-full max-w-screen-2xl items-center gap-3 px-4 py-2">
        <Bell className="h-4 w-4 shrink-0" aria-hidden />
        <p className="min-w-0 flex-1 text-xs">
          <span className="font-semibold">Turn on desktop notifications.</span>{' '}
          Get a pop-up for client replies, SLA breaches and assignments — even while you&apos;re
          in another tab.
        </p>
        <Button size="sm" onClick={() => void enable()} disabled={busy}>
          {mode === 'turn-on' ? 'Turn on' : 'Enable notifications'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="text-tint-info-fg hover:bg-tint-info-ring/30 hover:text-tint-info-fg"
          title="Hide this for 7 days"
        >
          Not now
        </Button>
      </div>
    </div>
  );
}
